
import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import {  useCall, useEthers } from '@usedapp/core';
import { ethers } from 'ethers';
import { hashMessage } from "@ethersproject/hash";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import {LoadingStack}  from './elements/LoadingStack';
import {ConnectButton}  from './ConnectButton';
import { buyTruthAddresses, endpoints } from '../shared/config'
import BuyTruthABI from '@buytruth/contracts/artifacts/contracts/BuyTruth.sol/BuyTruth.json';


const listStyle={ width: '100%', boxShadow: '0px 40px 120px 0px rgba(0, 0, 0, 0.5)', bgcolor: '#0c0c13', marginLeft: '0 !important', color: '#91919f !important', padding: "20px", borderRadius: '24px'};


interface ChainProps {
  account?: string;
  chainId?: number;
}

export const Charity = ({ account, chainId }: ChainProps) => {

  const navigate = useNavigate();
  const [activeNominations, setActiveNominations] = useState([]);
  const [successNomination, setSuccessNomination] = useState(false);

  const getActiveNominations = (forceUpdate: boolean) => {
    fetch(endpoints.api + '/charity/active_nominations').then(async (response: any) => {
      const nominationsArr = await response.json();
      
      if(forceUpdate || nominationsArr.length !== activeNominations.length){
        const sorted = nominationsArr.sort((a: any, b: any) => 
          b.voteCount - a.voteCount || a.name.localeCompare(b.name)
        );
        setActiveNominations(sorted);
      }
    });
  }

  if(activeNominations.length === 0){
    getActiveNominations(false);
  }

  const darkTheme = createTheme({
    palette: {
      mode: 'dark'
    }
  });


  return (
    <>
      <Grid container rowSpacing={2} columnSpacing={{ xs: 3, sm: 4, md: 5 }} columns={{ xs: 4, sm: 8, md: 12 }}>
        <Grid item xs={12} sx={{zIndex:2}}>
          <a style={{fontWeight: 'bold', color: '#fc9fff', textShadow: '0px 0px 5px #000'}} rel="noreferrer" onClick={()=>{navigate('/')}}>Back to balances</a>
        </Grid>
        <Grid item xs={8} sx={{zIndex:2}}>

          <h3 style={{marginTop:'50px', marginBottom:'20px',  textTransform: 'none'}}>Charitable Causes</h3>
          <List sx={listStyle}>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 2, sm: 3, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
              { 
                activeNominations.length === 0 ?
                  <div style={{width: '75%', marginTop: "30px", marginLeft: "25px"}}>
                    <h4>No approved nominations yet... Feel free to submit one.</h4>
                    <LoadingStack/>
                  </div>
                : <></>
              }
              <ThemeProvider theme={darkTheme}>
              {
                
                activeNominations.map(function(nom, i){
                  return <Nomination nomination={nom} account={account} chainId={chainId} onVote={getActiveNominations}/>;
                })
              }
              </ThemeProvider>
            </Grid>
          </List>
        </Grid>
        <Grid item xs={4} sx={{zIndex:2}}>
          <h3 style={{marginTop:'50px', marginBottom:'20px',  textTransform: 'none'}}>Nominate a Cause</h3>
          <List sx={listStyle}>
            { 
            !successNomination ? 
                <NominationForm account={account} chainId={chainId} onNomination={()=> { setSuccessNomination(true) }} />
              : 
              <div>Thanks for your nomination</div>
            }
          </List>
        </Grid>
      </Grid>
      
    </>
  );
};

interface NominationProps {
  nomination: any;
  account?: string;
  chainId?: number;
  onVote: Function;
}

export const Nomination = ({ nomination, account, chainId, onVote }: NominationProps) => {

  var isVotingActive = new Date(nomination.votingStarts).getTime() < new Date().getTime() && new Date().getTime() < new Date(nomination.votingEnds).getTime();
  const formatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

  const [errorMessage, setErrorMessage] = useState("");
  const { library } = useEthers();

  const getYoutubeThumbnail = (mediaLink: string) => {
    return "https://img.youtube.com/vi/" + youtubeLinkParser(mediaLink) + "/maxresdefault.jpg";
  }

  const submitVote = async (e: any) => {
    e.preventDefault();
    setErrorMessage("");

    try{

      const signer = (library as ethers.providers.Web3Provider).getSigner();
      const btContract: any = chainId && new ethers.Contract(buyTruthAddresses[chainId], BuyTruthABI.abi, library);

      var totalBalanceRaw = await btContract.balanceTotalOf(account);
      var balance = parseInt(ethers.utils.formatEther(totalBalanceRaw));
      console.log("TRUTH balance", balance);

      var message = "I vote for " + nomination.name + " as a charitable cause with " + formatter.format(balance) + " votes" ;
      let flatSig = await signer.signMessage(message);

      const sponsor = ethers.utils.recoverAddress(hashMessage(message), flatSig);

      if(sponsor !== account){
        setErrorMessage("Account signer mismatch");
        return;
      }

      const voteBody = {
        id: nomination._id,
        name: nomination.name,
        votes: balance,
        signature: flatSig
      };
    
      const response = await fetch(endpoints.api + '/charity/submit_vote', {
        method: 'post',
        body: JSON.stringify(voteBody),
        headers: {'Content-Type': 'application/json'}
      });
      const data = await response.json();

      if(data.status !== "OK"){
        setErrorMessage(data.status);
      } else {
        onVote(true);
      }

    } catch (e: any){
      setErrorMessage(e.message);
    }
    
  }

  

  return (
    
      <Grid item xs={4}>
        <Card sx={{ marginBottom: "30px" }}>
          
          { nomination.mediaLink && (nomination.mediaLink.endsWith("jpg") || nomination.mediaLink.endsWith("png"))?
            <CardMedia
              component="img"
              height="140"
              image={nomination.mediaLink}
            /> : <></>
          }

          { nomination.mediaLink && nomination.mediaLink.includes("youtu")  ?
            <CardMedia
              component="img"
              height="140"
              image={getYoutubeThumbnail(nomination.mediaLink)}
            /> : <></>
          }

          <CardContent sx={{bgcolor: "#11111c"}}>
            <Typography gutterBottom variant="h5" component="div">
              {nomination.name}
            </Typography>
            { isVotingActive ? 
              <Typography gutterBottom variant="subtitle1" color="text.primary" component="div">
                <b>Votes: {formatter.format(nomination.voteCount)}</b>
              </Typography>
              : <></> 
            }
            <Typography variant="body2" color="text.secondary">
              {nomination.description}
            </Typography>
          </CardContent>
          <CardActions sx={{bgcolor: "#11111c"}}>
            {
              nomination.contactInfo.includes("@") ?
              <Button size="small" href={"mailto:" + nomination.contactInfo}>Email</Button>
              : <></>
            }

            {
              nomination.contactInfo.includes("http") ?
              <Button size="small" href={nomination.contactInfo}>Link</Button>
              : <></>
            }

            { nomination.mediaLink && (nomination.mediaLink.includes("youtu") || nomination.mediaLink.includes("vimeo")) ? 
              <Button size="small" href={nomination.mediaLink}>Video</Button>
              : <></>
            }

            { isVotingActive ? 
              <Button size="small" sx={{color: "#fc9fff"}} onClick={submitVote}>Vote</Button>
              : <></>
            }
          </CardActions>
          { errorMessage ? 
            <CardContent sx={{bgcolor: "#11111c"}}>
              <Typography variant="body2" color="red">
                {errorMessage}
              </Typography>
            </CardContent>
            : <></>
          }   
        </Card>
      </Grid>
    
  );
};

interface NominationFormProps {
  account?: string;
  chainId?: number;
  onNomination: Function;
}

export const NominationForm = ({ account, chainId, onNomination }: NominationFormProps) => {

  const [causeName, setCauseName] = useState("");
  const [causeContact, setCauseContact] = useState("");
  const [causeMedia, setCauseMedia] = useState("");
  const [causeDescription, setCauseDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { library } = useEthers();
  const btContract = chainId && new ethers.Contract(buyTruthAddresses[chainId], BuyTruthABI.abi);

  const totalBalanceResponse = useCall(account && btContract && {
    contract: btContract,
    method: 'balanceTotalOf',
    args: [account],
  });

  const submitNomination = async (e: any) => {
    e.preventDefault();
    setErrorMessage("");

    if(library && totalBalanceResponse){
      try{

        if(!causeName || !causeContact || !causeDescription ){
          setErrorMessage("Complete all required fields");
          return;
        }

        if(totalBalanceResponse.error){
          setErrorMessage("Error retrieving balance");
          return;
        }

        var truthBalance = parseFloat(ethers.utils.formatEther(totalBalanceResponse.value[0]));

        if(truthBalance < 1000000){
          setErrorMessage("Insufficient TRUTH to nominate. Need at least 1M TRUTH");
          return;
        }

        const signer = (library as ethers.providers.Web3Provider).getSigner();

        var message = "I nominate " + causeName + " as a charitable cause";
        let flatSig = await signer.signMessage(message);

        const sponsor = ethers.utils.recoverAddress(hashMessage(message), flatSig);

        if(sponsor !== account){
          setErrorMessage("Account signer mismatch");
          return;
        }

        const body = {
          name: causeName,
          contactInfo: causeContact,
          mediaLink: causeMedia,
          description: causeDescription,
          signature: flatSig
        };
      
        const response = await fetch(endpoints.api + '/charity/submit_nomination', {
          method: 'post',
          body: JSON.stringify(body),
          headers: {'Content-Type': 'application/json'}
        });
        const data = await response.json();

        if(data.status !== "OK"){
          setErrorMessage(data.status);
        } else {
          onNomination();
        }

      } catch (e: any){
        setErrorMessage(e.message);
      }
    }
  }

  const acknoledgementStyle = {fontSize: "14px", color: "#bbb"};
  const unorderedListStyle = {marginLeft: "20px", listStyleType: "circle"};

  const validate = (e: any) => {
    if(e.target.required && e.target.value === ""){
      e.target.classList.add("error");
    } else {
      e.target.classList.remove("error");
    }
  }

  return (
    <>
      { account && chainId ?
          <>
              <form className="first__form form">
                  <div className="form__row">
                    <div className="form__input">
                      <label>
                        <input style={{color: '#fff'}} type="text" className="item_field" placeholder="Cause name" value={causeName} required={true} onChange={(e) => { setCauseName(e.target.value) }} onBlur={validate} />
                      </label>
                    </div>
                  </div>
                  <div className="form__row">
                    <div className="form__input">
                      <label>
                        <input style={{color: '#fff'}} type="text" className="item_field" placeholder="Email / Website / Social" value={causeContact} required={true} onChange={(e) => { setCauseContact(e.target.value) }} onBlur={validate} />
                      </label>
                    </div>
                  </div>
                  <div className="form__row">
                    <div className="form__input">
                      <label>
                        <input style={{color: '#fff'}} type="text" className="item_field" placeholder="Media URL (YouTube, Image, etc) - Optional" value={causeMedia} required={false} onChange={(e) => { setCauseMedia(e.target.value) }} onBlur={validate} />
                      </label>
                    </div>
                  </div>
                  <div className="form__row">
                    <div className="form__area">
                      <label>
                        <textarea style={{color: '#fff'}} className="item_field" placeholder="Provide details about the cause (to be displayed to voters)" value={causeDescription} required={true} onChange={(e) => { setCauseDescription(e.target.value) }}  onBlur={validate} ></textarea>
                      </label>
                    </div>
                  </div>
                  <div>
                    <p style={acknoledgementStyle}>I acknowledge the following:</p>
                    <ul style={acknoledgementStyle}>
                      <li style={unorderedListStyle}>A minimum of 1M TRUTH is required to nominate a cause</li>
                      <li style={unorderedListStyle}>All causes will be vetted to prevent abuse/fraud</li>
                      <li style={unorderedListStyle}>Only one nomination can be made per nomination session</li>
                      <li style={unorderedListStyle}>Nomination rules are subject to change</li>
                    </ul>
                    <p style={acknoledgementStyle}>I certify the following:</p>
                    <ul style={acknoledgementStyle}>
                      <li style={unorderedListStyle}>The above nomination supports a Christian cause</li>
                      <li style={unorderedListStyle}>The nomination has publicly verifiable contact information</li>
                    </ul>
                  </div>
                  <div className="form__submit">
                    <button onClick={submitNomination} disabled={false}><span>Submit</span></button>
                    {errorMessage ? <p style={{color:'red', marginTop: "15px", fontSize: "14px"}}>{errorMessage}</p> : <></>}
                  </div>	
              </form>
            
          </>
        : <>
            <ConnectButton/>
            <h3 style={{marginTop:'20px', fontSize: '32px', textAlign: 'center'}}>Connect your wallet to nominate a cause</h3>
            <LoadingStack/>
        </> }
    </>
  );
};



function youtubeLinkParser(url: string) {
  var regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;
  var match = url.match(regExp);
  if (match && match[2].length === 11) {
      return match[2];
  } else {
      return null;
  }
}